import { FullSmartheartUser } from "../api/index.defs";
import {
  IAuthenticatedUser,
  ICreateAuthenticatedUser
} from "../models/authenticated-user.interface";
import { Permissions } from "../models/permission";
import Role from "../models/role";
import { ITokenUser } from "../models/token.interface";
import { OrgType } from "../models/users.interface";
import {
  getPermissionsFromServerPermissions,
  getRoleFromServerRoleAndOrg,
  mapServerUserToCreateAuthUser,
  mapTokenUserToCreateAuthUser
} from "../utils/authorizationUtil";

const createAuthenticatedUser = (
  user: ITokenUser | FullSmartheartUser
): IAuthenticatedUser => {
  let createAuthUser: ICreateAuthenticatedUser;
  // If user type is ITokenUser
  if ((user as ITokenUser).UserId) {
    createAuthUser = mapTokenUserToCreateAuthUser(user as ITokenUser);
  } else {
    createAuthUser = mapServerUserToCreateAuthUser(user as FullSmartheartUser);
  }

  const {
    id,
    firstName,
    lastName,
    email,
    organization,
    canViewEcgs,
    canEditInterpretation,
    canManageNonProUsers,
    canManageProUsers,
    canManageEntities,
    canViewReports,
    canLogIntoPortal,
    canApproveEcg,
    clientAccessUserPermission,
    serverRoleId,
    title,
    isSso,
    signInUrl,
    signOutUrl,
    canSendEcgsViaMail,
    canViewSelfClinicsEcgs,
    showEcgPreview,
    isViewSelfClinicIsAllowed
  } = createAuthUser;

  const role = getRoleFromServerRoleAndOrg(serverRoleId, organization?.type);

  const permissions = getPermissionsFromServerPermissions({
    canViewEcgs,
    canEditInterpretation,
    canManageNonProUsers,
    canManageProUsers,
    canManageEntities,
    canViewReports,
    canLogIntoPortal,
    canApproveEcg,
    canSendEcgsViaMail,
    canViewSelfClinicsEcgs,
    showEcgPreview,
    isViewSelfClinicIsAllowed
  });

  const authenticatedUser: IAuthenticatedUser = {
    id,
    firstName,
    lastName,
    email,
    clientAccessUserPermission,
    role,
    permissions,
    organization,
    title,
    isSso,
    signInUrl,
    signOutUrl,
  };

  return authenticatedUser;
};

const hasRole = (userRole: Role, roles: Role[]): boolean => {
  if (!userRole) return false;
  return roles.includes(userRole);
};

function hasPermissions(
  userPermissions: Permissions[],
  desiredPermissions: Permissions[] | Permissions[][]
): boolean {
  const isPermissionsMatrix =
    Array.isArray(desiredPermissions) && Array.isArray(desiredPermissions[0]);
  const permissionIncludeFn = (dp: Permissions[]) =>
    dp.every((p) => userPermissions.includes(p));
  return isPermissionsMatrix
    ? (desiredPermissions as Permissions[][]).some(permissionIncludeFn)
    : permissionIncludeFn(desiredPermissions as Permissions[]);
}

const hasOrgType = (
  userOrgType: OrgType,
  desiredOrgTypes: OrgType[]
): boolean => {
  return desiredOrgTypes.includes(userOrgType);
};

const authorizationService = {
  createAuthenticatedUser,
  hasPermissions,
  hasRole,
  hasOrgType,
};

export default authorizationService;
